import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Starter, StarterTexture, FeedingRatio, TEXTURE_RATIOS } from '../../types/baking';
import { TextureSelector } from './TextureSelector';
import { StorageSelector } from './StorageSelector';
import { ManagementStrategySelector } from './ManagementStrategySelector';

interface NewStarterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (starterData: Omit<Starter, 'id' | 'createdAt'>) => void;
}

const initialFormData = {
  name: '',
  texture: 'liquid' as StarterTexture,
  feedingRatio: '1-1-1' as FeedingRatio,
  flourType: '',
  storage: 'room',
  roomTemperature: 20,
  lastFed: new Date(),
  isFavorite: false,
  managementStrategy: 'normal'
};

export function NewStarterModal({ isOpen, onClose, onSubmit }: NewStarterModalProps) {
  const [formData, setFormData] = useState(initialFormData);

  // Reset form data when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      const defaultRatio = TEXTURE_RATIOS[initialFormData.texture][0];
      setFormData({
        ...initialFormData,
        feedingRatio: defaultRatio
      });
    }
  }, [isOpen]);

  // Update feeding ratio when texture changes
  useEffect(() => {
    const defaultRatio = TEXTURE_RATIOS[formData.texture][0];
    if (formData.feedingRatio !== defaultRatio) {
      setFormData(prev => ({
        ...prev,
        feedingRatio: defaultRatio
      }));
    }
  }, [formData.texture]);

  const handleSubmit = () => {
    onSubmit(formData);
    setFormData(initialFormData); // Reset form after submission
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <div className="bg-gradient-to-br from-[#1C1C1C] to-[#888888] shadow-lg backdrop-blur-xl w-full max-w-lg max-h-[85vh] flex flex-col text-white">
        <div className="p-6 border-b border-white/10">
          <div className="flex justify-between items-center">
            <div className="flex-1">
              <h2 className="text-2xl text-white font-brand font-light italic">New Starter</h2>
            </div>
            <button 
              onClick={onClose} 
              className="text-white/70 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-full"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-white/90 mb-1">
                Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/50 focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20"
                placeholder="e.g., Whole Wheat Starter"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/90 mb-2">
                Texture
              </label>
              <TextureSelector
                value={formData.texture}
                onChange={(texture) => setFormData({ ...formData, texture })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/90 mb-2">
                Feeding Ratio (Starter-Flour-Water)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {TEXTURE_RATIOS[formData.texture].map((ratio) => (
                  <button
                    key={ratio}
                    onClick={() => setFormData({ ...formData, feedingRatio: ratio })}
                    className={`p-4 border rounded-xl flex items-center gap-3 transition-colors ${
                      formData.feedingRatio === ratio
                        ? 'border-amber-400/50 bg-gradient-to-b from-amber-400/20 to-amber-500/10'
                        : 'border-white/10 bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    <span className="text-white">{ratio.replace(/-/g, ':')}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/90 mb-1">
                Flour Type
              </label>
              <input
                type="text"
                value={formData.flourType}
                onChange={(e) => setFormData({ ...formData, flourType: e.target.value })}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/50 focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20"
                placeholder="e.g., Whole Wheat, Rye, All-Purpose"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/90 mb-2">
                Management Strategy
              </label>
              <ManagementStrategySelector
                value={formData.managementStrategy}
                storage={formData.storage}
                onChange={(strategy) => setFormData({ ...formData, managementStrategy: strategy })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/90 mb-2">
                Storage Location
              </label>
              <StorageSelector
                value={formData.storage}
                onChange={(storage) => setFormData({ ...formData, storage })}
                disableFridge={formData.managementStrategy === 'overboost'}
                roomTemperature={formData.storage === 'room' ? formData.roomTemperature : undefined}
                onRoomTemperatureChange={(temp) => setFormData({ ...formData, roomTemperature: temp })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/90 mb-1">
                Last Fed
              </label>
              <input
                type="datetime-local"
                value={new Date(new Date(formData.lastFed).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
                onChange={(e) => {
                  setFormData({ ...formData, lastFed: new Date(e.target.value) });
                }}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20"
              />
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-white/10 bg-white/5">
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-white/20 rounded-xl hover:bg-white/10 text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!formData.name || !formData.flourType}
              className={`px-6 py-2 rounded-xl transition-all duration-300 ${
                formData.name && formData.flourType
                  ? 'bg-gradient-to-r from-amber-500/90 to-amber-600/90 hover:from-amber-500 hover:to-amber-600 text-white'
                  : 'bg-white/10 text-white/50 cursor-not-allowed'
              }`}
            >
              Create Starter
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}